"use client"

/**
 * Email Agent Test Chat Dialog.
 *
 * Thin wrapper around the shared TestChatDialog that adds email-specific
 * context inputs (sender email, classification category, email subject)
 * and points at the email auto-reply test-chat endpoint.
 */

import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Mail } from "lucide-react"
import {
  TestChat as BaseTestChat,
  TestChatDialog as BaseTestChatDialog,
  type TestChatExperience,
  type TestChatProps as BaseTestChatProps,
} from "@/components/agents/shared/test-chat-dialog"

interface EmailTestChatProps {
  agentId: string
  agentName?: string
  useAiTools?: boolean
  experience?: TestChatExperience
}

interface EmailTestChatDialogProps extends EmailTestChatProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TestChat(props: EmailTestChatProps) {
  return <BaseTestChat {...useEmailTestChatProps(props)} />
}

export function TestChatDialog({
  open,
  onOpenChange,
  ...props
}: EmailTestChatDialogProps) {
  return (
    <BaseTestChatDialog
      {...useEmailTestChatProps(props)}
      open={open}
      onOpenChange={onOpenChange}
    />
  )
}

function useEmailTestChatProps(
  props: EmailTestChatProps,
): BaseTestChatProps {
  const [senderEmail, setSenderEmail] = useState("")
  const [classificationCategory, setClassificationCategory] = useState("")
  const [emailSubject, setEmailSubject] = useState("")

  return {
    ...props,
    apiEndpoint: "/api/agents/email/auto-reply/test-chat",
    buildRequestBody: ({ message, history, useTools }) => ({
      message,
      senderEmail: senderEmail.trim() || undefined,
      classificationCategory: classificationCategory.trim() || undefined,
      emailSubject: emailSubject.trim() || undefined,
      history,
      useTools,
      agent_id: props.agentId,
    }),
    inputPlaceholder: "Type test email content... (Press Enter to send)",
    toolsEnabledLabel: "AI tools (order lookup, escalation) enabled.",
    contextInputs: (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <Input
            type="email"
            placeholder="Sender email"
            value={senderEmail}
            onChange={(event) => setSenderEmail(event.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <Input
          type="text"
          placeholder="Classification category"
          value={classificationCategory}
          onChange={(event) => setClassificationCategory(event.target.value)}
          className="h-8 text-sm"
        />
        <Input
          type="text"
          placeholder="Email subject"
          value={emailSubject}
          onChange={(event) => setEmailSubject(event.target.value)}
          className="h-8 text-sm"
        />
      </div>
    ),
  }
}
